<template>
  <Teleport to="body">
    <transition name="shorts-carousel-modal-backdrop-fade" @after-enter="afterEnter">
      <div
        v-if="visible"
        ref="modal"
        class="shorts-carousel-modal-backdrop"
        :class="activeModal"
        tabindex="0"
        @keydown.esc="handleCloseModal"
      >
        <transition name="shorts-carousel-modal-content-fade">
          <div v-if="showContent" class="shorts-carousel-modal" @click.stop>
            <ol ref="track" class="shorts-carousel-modal-track">
              <template v-for="(item, index) in items" :key="index">
                <li
                  ref="shortCarouselItems"
                  class="shorts-carousel-modal-item"
                  :class="[currentIndex === index && 'is--active']"
                >
                  <YoutubePlayer
                    :is-dragging="currentIndex === index && isDragging"
                    :autoplay="currentIndex === index"
                    :active="currentIndex === index"
                    :video-id="item.shortsId"
                    :poster="item.image"
                    @click="handleVideoClick()"
                    @complete="handleGoToSlide('next')"
                  />
                </li>
              </template>
            </ol>
          </div>
        </transition>

        <button
          class="shorts-carousel-modal-close"
          aria-label="Overlay sluiten"
          @click="handleCloseModal"
        >
          <Icon name="eenvandaag-general:x-close" class="icon" role="presentation" />
        </button>

        <div class="shorts-carousel-modal-nav">
          <div class="shorts-carousel-modal-nav-prev">
            <span v-if="currentIndex !== 0" class="shorts-carousel-modal-nav-tooltip">
              Vorige video
            </span>
            <Button
              aria-label="Vorige short video"
              :disabled="currentIndex === 0"
              :tabindex="currentIndex !== 0 ? 0 : -1"
              variant="primary"
              type="icon"
              size="large"
              icon="eenvandaag-general:chevron-up"
              @click="handleGoToSlide('prev')"
            />
          </div>
          <div class="shorts-carousel-modal-nav-next">
            <span
              v-if="currentIndex !== items.length - 1"
              class="shorts-carousel-modal-nav-tooltip"
            >
              Volgende video
            </span>
            <Button
              aria-label="Volgende short video"
              :disabled="currentIndex === items.length - 1"
              :tabindex="currentIndex !== items.length - 1 ? 0 : -1"
              type="icon"
              size="large"
              variant="primary"
              icon="eenvandaag-general:chevron-down"
              @click="handleGoToSlide('next')"
            />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
const { $gsap: gsap, $Draggable: Draggable } = useNuxtApp();

const showContent = ref(false);
const modal = ref<HTMLElement | undefined>(undefined);

// Draggable elements
const track = ref<HTMLElement | null>(null);
const draggableInstance = ref<Draggable | null>(null);
const itemHeight = ref(0);
const shortCarouselItems = ref<Array<HTMLElement> | null>(null);
const currentIndex = ref(0);
const isDragging = ref(false);

const emit = defineEmits(['closeModal']);

const handleVideoClick = () => {
  // IOS HACK
  return;
};

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  activeItem: {
    type: Number,
    default: null,
  },
  items: {
    type: Array as () => Array<unknown>,
    default: () => [],
  },
});

const reactiveVisible = toRef(props, 'visible'); // Stays reactive!

// Watch for changes in the visible prop and set the showContent value to false
// This is needed to reset the showContent value to false when the modal is closed
watch(
  () => props.visible,
  (visible) => {
    bodyScrollLock(visible);

    if (visible) {
      window.addEventListener('keydown', handleKeyNavigation);
      window.addEventListener('wheel', handleScrollNavigation, { passive: true });
    } else {
      window.removeEventListener('keydown', handleKeyNavigation);
      window.removeEventListener('wheel', handleScrollNavigation);
      showContent.value = false;
    }
  },
);

const afterEnter = () => {
  bodyScrollLock(true);
  showContent.value = true;
  currentIndex.value = props.activeItem;
  // focus the modal when it is opened so that the user can use the keyboard to navigate / close the modal
  modal.value?.focus();

  nextTick(() => {
    initDraggable();
  });
};

const handleKeyNavigation = (e: KeyboardEvent) => {
  if (!props.visible) return;

  if (e.key === 'ArrowUp') {
    e.preventDefault();
    handleGoToSlide('prev');
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    handleGoToSlide('next');
  }
};

let scrollTimeout: number | null = null;

const handleScrollNavigation = (e: WheelEvent) => {
  if (!props.visible || scrollTimeout) return;

  const delta = e.deltaY;
  if (delta > 0) handleGoToSlide('next');
  else if (delta < 0) handleGoToSlide('prev');

  scrollTimeout = window.setTimeout(() => {
    scrollTimeout = null;
  }, 500); // half-second cooldown
};

const activeModal = showContent.value ? 'modal-is--enabled' : null;

const calculateDimensions = () => {
  const firstItem = shortCarouselItems.value && shortCarouselItems.value[0];

  // Get width of first child so we know the size of the items
  if (firstItem) {
    itemHeight.value =
      firstItem.clientHeight + parseFloat(getComputedStyle(firstItem).marginBottom);
  }

  updateCurrentIndex();
};

const updateCurrentIndex = () => {
  const newY = draggableInstance?.value?.y || 0;

  const closestIndex = Math.round(Math.abs(newY) / itemHeight.value);

  currentIndex.value = closestIndex === -1 ? props.items.length - 1 : closestIndex;
};

const goToSlide = (index: number, instant: boolean = false) => {
  const newOffset = -itemHeight.value * index;
  isDragging.value = true;

  gsap.to(track.value, {
    y: newOffset,
    duration: instant ? 0 : 0.5,
    ease: 'ease.inOut',
    onUpdate: () => {
      currentIndex.value = index;
      isDragging.value = false;
    },
  });
};

const handleGoToSlide = (direction: string) => {
  if (direction === 'prev') {
    if (currentIndex.value === 0) return;
    goToSlide(currentIndex.value - 1);
  }

  if (currentIndex.value === props.items.length - 1) return;

  if (direction === 'next') {
    goToSlide(currentIndex.value + 1);
  }
};

const initDraggable = (): void => {
  calculateDimensions();
  const snapPoints = props.items.map((_, i) => -(itemHeight.value * i));
  let direction: number = 0;

  // We need to create an Array of the draggable so we can target it later
  const [draggable] = Draggable.create(track.value, {
    dragResistance: 0.4,
    edgeResistance: 0,
    throwResistance: 0.5,
    maxDuration: 0.5,
    type: 'y',
    bounds: {
      minY: snapPoints[props.items.length - 1],
      maxY: 0,
    },
    inertia: true,
    dragClickables: true,
    zIndexBoost: false,

    onClick: (e) => {
      e.preventDefault(); // IOS HACK
    },
    onDrag() {
      isDragging.value = true;
      direction = draggableInstance.value?.deltaY || 0;
    },
    onDragEnd() {
      isDragging.value = false;
    },
    snap: {
      y: () => {
        if (direction < 0 && currentIndex.value < props.items.length - 1) {
          currentIndex.value += 1;
        } else if (direction > 0 && currentIndex.value > 0) {
          currentIndex.value -= 1;
        }
        return snapPoints[currentIndex.value];
      },
    },
  });

  // We are setting the first item the draggable array as our draggable instance so we can use that
  draggableInstance.value = draggable || null;

  goToSlide(props.activeItem, true);

  window.addEventListener('resize', onResizeHandler);
};

const calculateBounds = () => {
  return {
    minY: -itemHeight.value * (props.items.length - 1) || 0,
    maxY: 0,
  };
};

const onResizeHandler = () => {
  // On Resize firs calculate all new sizes
  calculateDimensions();
  draggableInstance.value?.applyBounds(calculateBounds());
  draggableInstance.value?.update();
  goToSlide(currentIndex.value);
};

const handleCloseModal = () => {
  emit('closeModal', true);
};

useFocusTrap(modal, reactiveVisible);
</script>

<style scoped lang="postcss">
.shorts-carousel-modal-backdrop {
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(0deg, rgba(0, 10, 69, 0.94) 0.03%, rgba(0, 8, 54, 0.94) 99.97%);
  backdrop-filter: blur(16px);
  outline: 0;
  z-index: 99;
}

.shorts-carousel-modal {
  height: 100%;
  width: 100%;
  aspect-ratio: 1118/1987;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.shorts-carousel-modal-track {
  --shorts-carousel-modal-track-height: 100%;
  --shorts-carousel-modal-track-width: 100%;
  --shorts-carousel-modal-track-padding: 104px 52px;

  display: flex;
  position: fixed;
  bottom: 0;
  flex-direction: column;
  align-items: center;
  width: var(--shorts-carousel-modal-track-width);
  height: var(--shorts-carousel-modal-track-height);
  padding: var(--shorts-carousel-modal-track-padding);
}
.shorts-carousel-modal-nav {
  --shorts-carousel-modal-nav-position-small: var(--unit-24);
  --shorts-carousel-modal-nav-position-large: var(--unit-32);
  --shorts-carousel-modal-nav-gap: var(--unit-16);

  position: absolute;
  display: flex;
  flex-direction: row;
  bottom: var(--shorts-carousel-modal-nav-position-small);
  gap: var(--shorts-carousel-modal-nav-gap);

  @media (--md-viewport) {
    top: 50%;
    flex-direction: column;
    bottom: auto;
    translate: 0 -50%;
    right: var(--shorts-carousel-modal-nav-position-large);
  }
}

.shorts-carousel-modal-nav-prev,
.shorts-carousel-modal-nav-next {
  position: relative;

  @add-mixin hover {
    @media (--lg-viewport) {
      .shorts-carousel-modal-nav-tooltip {
        opacity: 1;
      }
    }
  }
}

.shorts-carousel-modal-nav-tooltip {
  --shorts-carousel-modal-nav-tooltip-right-offset: calc(100% + 16px);
  --shorts-carousel-modal-nav-tooltip-font-size: 15px;
  --shorts-carousel-modal-nav-tooltip-padding: var(--unit-8) var(--unit-12);
  --shorts-carousel-modal-nav-tooltip-arrow-size: 7px;

  opacity: 0;
  position: absolute;
  width: auto;
  right: var(--shorts-carousel-modal-nav-tooltip-right-offset);
  top: 50%;
  transform: translate(0, -50%);
  background: #00106e;
  color: hsl(var(--color-neutral-white));
  font-size: var(--shorts-carousel-modal-nav-tooltip-font-size);
  white-space: nowrap;
  transition: opacity 250ms ease-in-out;
  display: none;
  border-radius: 4px;
  padding: var(--shorts-carousel-modal-nav-tooltip-padding);
  justify-content: center;
  align-items: center;

  &::after {
    content: ' ';
    position: absolute;
    top: 50%;
    left: 100%;
    margin-top: calc(-1 * var(--shorts-carousel-modal-nav-tooltip-arrow-size));
    border-width: var(--shorts-carousel-modal-nav-tooltip-arrow-size);
    border-style: solid;
    border-color: transparent transparent transparent #00106e;
  }

  @media (--lg-viewport) {
    display: flex;
  }
}

.shorts-carousel-modal-item {
  --shorts-carousel-modal-item-width: auto;
  --shorts-carousel-modal-item-min-width: 90%;
  --shorts-carousel-modal-item-height: 100%;
  --shorts-carousel-modal-item-scale: 0.95;
  --shorts-carousel-modal-item-active-scale: 1;
  --shorts-carousel-modal-item-active-box-shadow: 0px 32px 40px 0px rgba(0, 0, 0, 0.35);
  --shorts-carousel-modal-item-border: 2px solid hsl(var(--color-primary-700));
  --shorts-carousel-modal-active-item-border: 4px solid hsl(var(--color-primary-700));
  --shorts-carousel-modal-item-overlay: rgba(0, 0, 0, 0.6);

  height: var(--shorts-carousel-modal-item-height);
  width: var(--shorts-carousel-modal-item-width);
  flex-shrink: 0;

  min-width: var(--shorts-carousel-modal-item-min-width);

  @media (--md-viewport) {
    --shorts-carousel-modal-item-min-width: auto;
  }

  :deep(.youtube-container) {
    color: hsl(var(--color-neutral-white));

    pointer-events: none;
    scale: var(--shorts-carousel-modal-item-scale);
    transform-origin: center center;
    transition:
      box-shadow 0.2s ease-in-out,
      scale 0.4s ease-in-out;
    border: var(--shorts-carousel-modal-item-border);

    &:after {
      content: '';
      pointer-events: none;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: hsla(var(--color-primary-700), 0.5);
      z-index: 1;
      opacity: 1;
      transition: opacity 250ms ease-in-out;
    }
  }

  &.is--active {
    :deep(.youtube-container) {
      pointer-events: auto;
      border: var(--shorts-carousel-modal-active-item-border);
      box-shadow: var(--shorts-carousel-modal-item-active-box-shadow);
      scale: var(--shorts-carousel-modal-item-active-scale);

      &:after {
        opacity: 0;
      }
    }
  }
}

.shorts-carousel-modal-close {
  background: unset;
  border: unset;
  padding: unset;

  > .icon {
    display: block;
  }

  --shorts-carousel-modal-close-size: var(--unit-24);
  --shorts-carousel-modal-close-color: hsl(var(--color-neutral-white));
  --shorts-carousel-modal-close-opacity: 0.5;
  --shorts-carousel-modal-close-font-size: var(--unit-24);
  --shorts-carousel-modal-close-right: var(--unit-18);
  --shorts-carousel-modal-close-top: var(--unit-18);

  width: var(--shorts-carousel-modal-close-size);
  height: var(--shorts-carousel-modal-close-size);
  color: hsl(var(--color-neutral-white));
  font-size: var(--shorts-carousel-modal-close-font-size);
  position: absolute;
  cursor: pointer;
  right: var(--shorts-carousel-modal-close-right);
  top: var(--shorts-carousel-modal-close-top);

  @media (--md-viewport) {
    --shorts-carousel-modal-close-size: var(--unit-28);
    --shorts-carousel-modal-close-font-size: var(--unit-28);
    --shorts-carousel-modal-close-right: var(--unit-32);
    --shorts-carousel-modal-close-top: var(--unit-32);
  }
}

.shorts-carousel-modal-backdrop-fade-enter-active,
.shorts-carousel-modal-backdrop-fade-enter,
.shorts-carousel-modal-backdrop-fade-leave-active {
  transition: opacity 300ms ease;
}

.shorts-carousel-modal-backdrop-fade-leave-to,
.shorts-carousel-modal-backdrop-fade-enter-from {
  opacity: 0;
}

.shorts-carousel-modal-content-fade-enter-active,
.shorts-carousel-modal-content-fade-enter,
.shorts-carousel-modal-content-fade-leave-active {
  transition: opacity 300ms ease;
}

.shorts-carousel-modal-content-fade-leave-to,
.shorts-carousel-modal-content-fade-enter-from {
  opacity: 0;
}
</style>
